<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="$t('user_manage.add_member')"
    :modal="true"
    class="w-full max-w-4xl"
  >
    <TabView v-model:activeIndex="currentUserTab">
      <TabPanel :header="$t('user_manage.single_user')">
        <div class="space-y-4">
          <div
            v-for="(user, index) in inputArray"
            :key="index"
            class="grid grid-cols-1 gap-4 p-4 border rounded"
          >
            <div class="space-y-4 min-w-full">
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  $t("user_manage.firstname")
                }}</label>
                <InputText v-model="user.firstname" class="w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  $t("user_manage.lastname")
                }}</label>
                <InputText v-model="user.lastname" class="w-full" />
              </div>
            </div>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  $t("user_manage.email_label")
                }}</label>
                <InputText v-model="user.email" type="email" class="w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  $t("user_manage.phone_label")
                }}</label>
                <PhoneInput v-model="user.phone" class="w-full" />
              </div>
            </div>
            <div class="md:col-span-2">
              <label class="block text-sm font-medium mb-1">{{
                $t("user_manage.service")
              }}</label>
              <Dropdown
                v-model="user.service"
                :options="services"
                optionLabel="name"
                optionValue="name"
                :placeholder="$t('user_manage.select_service')"
                class="w-full"
              />
            </div>
            <div class="md:col-span-2 flex gap-2">
              <Button
                :label="$t('user_manage.add_row')"
                icon="pi pi-plus"
                @click="addRow(index)"
                class="p-button-sm spect-btn-outline"
                outlined
              />
              <Button
                :label="$t('user_manage.remove_row')"
                icon="pi pi-minus"
                @click="removeRow(index)"
                class="p-button-sm p-button-danger"
                outlined
                :disabled="inputArray.length === 1"
              />
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel :header="$t('user_manage.bulk_user')">
        <div class="space-y-4">
          <div class="flex gap-2 mb-4">
            <Button
              :label="$t('user_manage.download_template')"
              icon="pi pi-download"
              @click="downloadTemplate"
              class="p-button-sm p-button-outlined spect-btn-outline"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">{{
              $t("user_manage.upload_csv")
            }}</label>
            <FileUpload
              mode="basic"
              accept=".csv"
              :maxFileSize="1000000"
              @select="handleFileUpload"
              :auto="true"
              :chooseLabel="$t('user_manage.choose_csv_file')"
              class="spect-btn-primary"
            />
          </div>

          <div v-if="tableData.length > 0">
            <h4 class="font-semibold mb-2">
              {{ $t("user_manage.preview_data") }}
            </h4>
            <DataTable
              :value="tableData"
              class="p-datatable-sm"
              :paginator="true"
              :rows="5"
            >
              <Column
                :field="tableField.firstname.field"
                :header="$t('user_manage.firstname')"
              ></Column>
              <Column
                :field="tableField.lastname.field"
                :header="$t('user_manage.lastname')"
              ></Column>
              <Column
                :field="tableField.email.field"
                :header="$t('user_manage.email_label')"
              ></Column>
              <Column
                :field="tableField.phone.field"
                :header="$t('user_manage.phone_label')"
              ></Column>
              <Column
                :field="tableField.service.field"
                :header="$t('user_manage.service')"
              ></Column>
              <Column :header="$t('common.actions')">
                <template #body="slotProps">
                  <Button
                    icon="pi pi-trash"
                    class="p-button-rounded p-button-text p-button-danger p-button-sm"
                    @click="deleteRow(slotProps.index)"
                  />
                </template>
              </Column>
            </DataTable>
            <Button
              :label="$t('user_manage.reset_data')"
              icon="pi pi-refresh"
              @click="resetTableData"
              class="mt-2 p-button-sm p-button-secondary spect-btn-outline"
              outlined
            />
          </div>
        </div>
      </TabPanel>
    </TabView>

    <template #footer>
      <div class="flex gap-2">
        <Button
          :label="$t('common.cancel')"
          @click="closeModal"
          class="p-button-text primary-text"
        />
        <Button
          :label="$t('user_manage.add_members')"
          @click="saveMember"
          class="spect-btn-primary"
          :disabled="!isFormValid"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";
import Dialog from "primevue/dialog";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import FileUpload from "primevue/fileupload";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import PhoneInput from "@/components/PhoneInput.vue";
//import { useI18n } from 'vue-i18n'

//const { t } = useI18n()

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  services: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:visible", "save"]);

// Local reactive data
const currentUserTab = ref(0);
const inputArray = ref([
  {
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    service: "",
  },
]);
const tableData = ref([]);

// Computed properties
const tableField = computed(() => {
  const language = localStorage.getItem("language") || "fr";
  return language === "fr"
    ? {
        firstname: { field: "prénom", header: "Prénom" },
        lastname: { field: "nom", header: "Nom" },
        email: { field: "email", header: "E-mail" },
        phone: { field: "téléphone", header: "Téléphone" },
        service: { field: "service", header: "Service" },
      }
    : {
        firstname: { field: "firstname", header: "First Name" },
        lastname: { field: "lastname", header: "Last Name" },
        email: { field: "email", header: "Email" },
        phone: { field: "phone", header: "Phone" },
        service: { field: "service", header: "Service" },
      };
});
const isFormValid = computed(() => {
  if (currentUserTab.value === 0) {
    // Single user tab - check if at least one user has required fields
    return inputArray.value.some(
      (user) => user.firstname && user.lastname && user.email && user.service
    );
  } else {
    // Bulk user tab - check if table has data
    return tableData.value.length > 0;
  }
});

// Methods
const addRow = (index) => {
  inputArray.value.splice(index + 1, 0, {
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    service: "",
  });
};

const removeRow = (index) => {
  if (inputArray.value.length > 1) {
    inputArray.value.splice(index, 1);
  }
};

const downloadTemplate = () => {
  const language = localStorage.getItem("language") || "fr";
  // Add BOM marker for UTF-8 encoding to handle French characters correctly
  const BOM = "\ufeff";
  const csvContent =
    language === "fr"
      ? `${BOM}prénom,nom,email,téléphone,service\nJohn,Doe,john.doe@example.com,+1234567890,Service1\nJane,Smith,jane.smith@example.com,+0987654321,Service2`
      : `${BOM}firstname,lastname,email,phone,service\nJohn,Doe,john.doe@example.com,+1234567890,Service1\nJane,Smith,jane.smith@example.com,+0987654321,Service2`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "member_template.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleFileUpload = (event) => {
  const file = event.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target.result;
      const lines = csv.split("\n");
      const headers = lines[0].split(",");

      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(",");
          const row = {};
          let hasEmptyValue = false;
          // Check if we have enough values for all headers
          if (values.length !== headers.length) {
            continue; // Skip rows with incorrect number of values
          }
          // Build row object and check for empty values
          headers.forEach((header, index) => {
            const value = values[index] ? values[index].trim() : "";
            // Only check for empty values in all columns except the last one (service)
            if (!value && index < headers.length - 1) {
              hasEmptyValue = true;
            }
            row[header.trim()] = value;
          });
          // Only push row if all required values are present (excluding last column)
          if (!hasEmptyValue) {
            data.push(row);
          }
        }
      }
      tableData.value = data;
    };
    reader.readAsText(file);
  }
};

const deleteRow = (index) => {
  tableData.value.splice(index, 1);
};

const resetTableData = () => {
  tableData.value = [];
};

const closeModal = () => {
  emit("update:visible", false);
  resetForm();
};

const resetForm = () => {
  currentUserTab.value = 0;
  inputArray.value = [
    {
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      service: "",
    },
  ];
  tableData.value = [];
};

const saveMember = () => {
  const membersData =
    currentUserTab.value === 0
      ? inputArray.value
          .filter(
            (user) =>
              user.firstname && user.lastname && user.email && user.service
          )
          .map((user) => ({
            ...user,
            position: localStorage.getItem("organisation"),
          }))
      : tableData.value.map((item) => {
          // Convert French keys to English if language is French
          if (Object.keys(item).includes("prénom")) {
            return {
              firstname: item.prénom,
              lastname: item.nom,
              email: item.email,
              phone: item.téléphone,
              position: localStorage.getItem("organisation"),
              service: item.service,
            };
          }
          return item;
        });

  emit("save", {
    type: "add_member",
    data: membersData,
    isBulk: currentUserTab.value === 1,
  });
  closeModal();
};

// Watch for modal visibility changes
watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetForm();
    }
  }
);
</script>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss" as *;
@use "../../assets/scss/app/common.scss" as *;
</style>
